import type { LucideIcon } from 'lucide-react';
import clsx from 'clsx';

import { AnalysisMarkdown } from '../../../components';

/**
 * The tones these cards come in. Tessra's semantic colours stand in for the design's
 * per-card names (card-green, card-yellow, card-red), which the library does not define
 * but which resolve to the same families. Dark mode names the design's steps at full
 * strength: the light tints' 40% leaves them faint on a dark card, and Tessra's warning
 * is amber, which reads as brown there.
 */
type Tone = 'positive' | 'warning' | 'destructive';

const TONE_CLASS: Record<Tone, { surface: string; pill: string }> = {
  positive: {
    surface: 'bg-positive/40 dark:bg-green-950',
    pill: 'bg-positive text-positive-foreground dark:bg-green-900',
  },
  warning: {
    surface: 'bg-warning/40 dark:bg-yellow-950',
    pill: 'bg-warning text-warning-foreground dark:bg-badge-yellow',
  },
  destructive: {
    surface: 'bg-destructive-soft/40 dark:bg-red-950',
    pill: 'bg-destructive-soft text-destructive-soft-foreground dark:bg-badge-red',
  },
};

interface TintedListCardProps {
  tone: Tone;
  icon: LucideIcon;
  title: string;
  items: string[];
}

/**
 * A tinted card carrying a titled pill and a bulleted list: the shape Market Forces and
 * Current Performance Assessment both use for their two-up comparisons.
 *
 * The icon stays here, unlike elsewhere in the redesign, because it is directional rather
 * than decorative: it tells a reader which side of the pair they are looking at before
 * they read the label.
 */
export const TintedListCard = ({ tone, icon: Icon, title, items }: TintedListCardProps) => {
  const { surface, pill } = TONE_CLASS[tone];

  return (
    <div className={clsx('flex h-full flex-col gap-3 rounded-xl p-xl', surface)}>
      <div className="flex flex-col justify-center py-6">
        <span
          className={clsx(
            'inline-flex items-center gap-2 self-center rounded-full px-3.5 py-1.5',
            'text-label-md font-medium whitespace-nowrap mix-blend-multiply dark:mix-blend-normal dark:text-foreground',
            pill,
          )}
        >
          <Icon className="size-4 shrink-0" />
          {title}
        </span>
      </div>

      <ul className="flex flex-col gap-3">
        {items.map((item, i) => (
          <li key={`${item}-${i}`} className="flex items-start gap-3.5 pl-1.5">
            <span className="mt-[9px] size-1.5 shrink-0 rounded-full bg-foreground-subtle" />
            <AnalysisMarkdown text={item} className="text-body-md text-foreground" />
          </li>
        ))}
      </ul>
    </div>
  );
};
